import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GrCircleQuestion } from "react-icons/gr";
import { LuInfo, LuLink2, LuPenSquare } from "react-icons/lu";
import Tooltip from "../../Tooltip/Tooltip";
import moment from "moment";
import { setLoader } from "../../../features/LoaderSlice";
import { useIntl } from "react-intl";
import useCollect from "../../../hooks/useCollect";
import {
  isActionRestricted,
  isFieldRestricted,
} from "../../../features/configSlice";
import {
  ActionDeleteIdentity,
  ActionRefreshProfile,
  ActionRemoteConsentCollect,
  FieldContactEmail,
  FieldTitle,
} from "../../../utils/controls";
import Skeleton from "react-loading-skeleton";
import DetailsInHorizontal from "./DetailsInHorizontal";
import ActionButtonWithMenu from "../../ActionButtonWithMenu";
import { MdDeleteOutline, MdOutlineEmail } from "react-icons/md";
import { CgPushUp } from "react-icons/cg";
import useAuth from "../../../hooks/useAuth";
import PropTypes from "prop-types";
import useNotification from "../../../hooks/useNotification";
const ProfileCard = ({
  userDetails,
  controllerCodeValue,
  customerScope,
  setIsSentConsentOpen,
  setIsUpateOpen,
  getUserDetails,
  loading,
  handleOpenDeleteModal,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { isAdmin } = useAuth();
  const state = useSelector((state) => state.config);
  const { countryLanguages } = useSelector((state) => state.controller);
  const handleSendRemoteConsent = () => setIsSentConsentOpen(true);
  const handleUpdateConsent = () => setIsUpateOpen(true);
  const [language, setLanguage] = useState("");
  const { successToast, errorToast } = useNotification();
  const {
    identityCollect,
    handleCollectSuccessMessage,
    handleCollectErrorMessage,
  } = useCollect();
  const handleRefreshProfile = async () => {
    dispatch(setLoader({ loading: true, label: "" }));
    identityCollect(
      {
        controller_code: controllerCodeValue,
        customer_scope: customerScope,
        type: userDetails.type,
        value: userDetails.value,
        language: userDetails.language,
        contact_email: userDetails.contact_email,
        collection_channel: "Truman UI",
        last_update_channel: "Truman UI",
      },
      false,
      false,
      {},
      false
    )
      .then((res) => {
        getUserDetails();
        handleCollectSuccessMessage(res);
        dispatch(setLoader({ loading: false, label: "" }));
      })
      .catch((error) => {
        handleCollectErrorMessage(error);
        dispatch(setLoader({ loading: false, label: "" }));
      });
  };
  const handleDeleteIdentity = async () => handleOpenDeleteModal(true);
  const handleCopyLinkToProfile = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      successToast(
        intl.formatMessage({
          id: "Link Copied Successfully to Clipboard",
          defaultMessage: "Link Copied Successfully to Clipboard",
        })
      );
    } catch {
      errorToast(
        intl.formatMessage({
          id: "Error In Copying Link",
          defaultMessage: "Error In Copying Link",
        })
      );
    }
  };
  const userActionSettings = [
    {
      id: "editIdentity",
      label: intl.formatMessage({
        id: "id.details.edit-profile",
        defaultMessage: "Edit Profile",
      }),
      action: handleUpdateConsent,
      icon: <LuPenSquare />,
    },
    !isActionRestricted(state, ActionRemoteConsentCollect) && {
      id: "remoteConsent",
      label: intl.formatMessage({
        id: "label.send-remote-consent",
        defaultMessage: "Send Remote Consent",
      }),
      action: handleSendRemoteConsent,
      icon: <MdOutlineEmail />,
    },
    !isActionRestricted(state, ActionRefreshProfile) && {
      id: "pushToCRM",
      label: intl.formatMessage({
        id: "id.details.push-to-crm",
        defaultMessage: "Push to CRM",
      }),
      action: handleRefreshProfile,
      icon: <CgPushUp />,
    },
    !isActionRestricted(state, ActionRefreshProfile) && {
      id: "copyLinkToProfile",
      label: intl.formatMessage({
        id: "id.details.copy-link-to-profile",
        defaultMessage: "Copy Link To Profile",
      }),
      action: handleCopyLinkToProfile,
      icon: <LuLink2 />,
    },
    !isActionRestricted(state, ActionDeleteIdentity) &&
      isAdmin() && {
        id: "delete",
        label: "Delete Identity",
        icon: <MdDeleteOutline />,
        isDestructive: true,
        action: handleDeleteIdentity,
      },
  ].filter(Boolean);

  useEffect(() => {
    if (
      countryLanguages &&
      countryLanguages.length > 0 &&
      userDetails?.language
    ) {
      countryLanguages?.forEach((language) => {
        if (language?.value === userDetails?.language) {
          setLanguage(language?.label || userDetails?.language);
        }
      });
    }
  }, [countryLanguages, userDetails?.language]);
  return (
    <div className="d-flex flex-row shadow-sm p-3 rounded align-items-start" style={{ backgroundColor: "var(--theme-surface)", color: "var(--theme-text)" }}>
      <div className="flex-grow-1">
        <div className="d-flex flex-column gap-3">
          <h5 className="modal-title" id="exampleModalLongTitle">
            <div
              className="nameText fs-2"
              style={{ display: "flex", flexDirection: "row" }}
            >
              {}
              <span
                id="saluatation"
                className={userDetails?.salutation ? `pe-2` : ""}
                style={{ width: loading && 40, marginRight: loading && 8 }}
              >
                {loading ? <Skeleton /> : userDetails?.salutation}
              </span>
              <span id="userName" style={{ width: loading && 200 }}>
                {loading ? (
                  <Skeleton />
                ) : (
                  `${userDetails?.first_name || ""} ${
                    userDetails?.last_name || ""
                  }`
                )}
              </span>
            </div>
            <span className="text-secondary fs-6">
              {loading ? (
                <Skeleton width={300} />
              ) : (
                <>
                  <span className="pe-1">
                    <Tooltip position="left" text={"Date format: DD.MM.YYYY"}>
                      {intl.formatMessage({
                        id: "id.details.last-synced-on",
                        defaultMessage: "Last Synced on",
                      })}
                    </Tooltip>
                  </span>
                  <Tooltip
                    position="right"
                    text={`${moment
                      .utc(userDetails?.integration_update_datetime)
                      .format("DD.MM.YYYY HH:mm")} UTC`}
                  >
                    <span className="pe-1">
                      {" "}
                      {moment
                        .utc(userDetails?.integration_update_datetime)
                        .local()
                        .format("DD.MM.YYYY HH:mm")}
                    </span>
                    <LuInfo className="table__date__icon" size={19} />
                  </Tooltip>
                </>
              )}
            </span>
          </h5>
          {!isFieldRestricted(state, FieldTitle) && (
            <DetailsInHorizontal
              label={intl.formatMessage({
                id: "label.title",
                defaultMessage: "Title",
              })}
              value={userDetails?.title}
              loading={loading}
              width={150}
            />
          )}
          <DetailsInHorizontal
            label={`ID (${userDetails?.type || "Type"})`}
            value={userDetails?.value}
            loading={loading}
          />
          <DetailsInHorizontal
            label={intl.formatMessage({
              id: "label.language",
              defaultMessage: "Language",
            })}
            value={language}
            loading={loading}
          />
          {!isFieldRestricted(state, FieldContactEmail) && (
            <DetailsInHorizontal
              label={intl.formatMessage({
                id: "label.contact-email",
                defaultMessage: "contact-email",
              })}
              value={userDetails?.contact_email}
              tooltip={
                <Tooltip
                  text="This email will be used for all truman related communication"
                  type="profile-card"
                >
                  <GrCircleQuestion
                    className="table__date__icon ms-1"
                    size={19}
                  />
                </Tooltip>
              }
              loading={loading}
            />
          )}
        </div>
      </div>
      <ActionButtonWithMenu options={userActionSettings} loading={loading} />
    </div>
  );
};
ProfileCard.propTypes = {
  userDetails: PropTypes.object,
  controllerCodeValue: PropTypes.string,
  customerScope: PropTypes.string,
  setIsSentConsentOpen: PropTypes.func,
  setIsUpateOpen: PropTypes.func,
  getUserDetails: PropTypes.func,
  loading: PropTypes.bool,
  handleOpenDeleteModal: PropTypes.func,
};

export default ProfileCard;
